import { createFileRoute, notFound } from "@tanstack/react-router";

import { sdk } from "@/api/sdk";
import { BlogPage } from "@/features/blog/components/blog-chrome";
import { useLoadInBrowser } from "@/features/blog/hooks/use-load-in-browser";
import { BlogArticleView } from "@/features/blog/modules/blog-article/blog-article";
import { blogArticleHead } from "@/features/blog/utils/blog-head";
import { reportApiOutage } from "@/lib/api-outage";
import { m } from "@/paraglide/messages.js";
import { getUrlOrigin } from "@/paraglide/runtime.js";
import { BlogAudience } from "@complete-athlete/enums";

/**
 * One article, rendered on the server with everything a crawler reads. The article names its own
 * canonical address and languages (its translations), so the root adds neither.
 */
export const Route = createFileRoute("/(public)/blog/$slug/")({
  staticData: { ownAlternates: true, ownSocialImage: true },
  loader: async ({ params }) => {
    const { data, status } = await sdk.blog.posts.$slug.$get
      .setParams({ slug: params.slug })
      .setQueryParams({ audience: BlogAudience.COACH })
      .send();
    if (status === 404) throw notFound();
    // On the server a failed request hands the page to the browser, which asks again.
    if (!data) reportApiOutage();
    if (!data && typeof window !== "undefined") throw new Error(`Could not load this article (status ${status ?? "none"})`);
    return { article: data ?? null, crumb: data?.title ?? m.blog_title() };
  },
  head: ({ loaderData }) =>
    loaderData?.article ? blogArticleHead(loaderData.article, { origin: getUrlOrigin(), blogTitle: m.blog_title() }) : {},
  component: BlogArticlePage,
});

function BlogArticlePage() {
  const { article } = Route.useLoaderData();
  useLoadInBrowser(article);

  return <BlogPage>{article ? <BlogArticleView key={article.id + article.locale} article={article} /> : null}</BlogPage>;
}
